/**
 * Request Deduplication Enhancement for BaseService
 * Prevents multiple identical requests from executing simultaneously
 */

export interface DedupConfig {
  enabled: boolean;
  ttl: number; // Time in ms before a pending request is considered stale
  keyGenerator?: (args: any[]) => string;
}

export class RequestDeduplicator {
  private pendingRequests = new Map<string, { promise: Promise<any>; timestamp: number }>();
  private readonly DEFAULT_TTL = 5000; // 5 seconds

  constructor(private config: DedupConfig = { enabled: true, ttl: 5000 }) {}

  /**
   * Execute a request with deduplication
   */
  async execute<T>(
    key: string,
    operation: () => Promise<T>,
    ttl: number = this.config.ttl || this.DEFAULT_TTL,
  ): Promise<T> {
    if (!this.config.enabled) {
      return operation();
    }

    // Clean up stale requests
    this.cleanupStaleRequests();

    // Check for existing pending request
    const existing = this.pendingRequests.get(key);
    if (existing && Date.now() - existing.timestamp < ttl) {
      return existing.promise as Promise<T>;
    }

    // Create new request
    const promise = operation();
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
    });

    try {
      const result = await promise;
      this.pendingRequests.delete(key);
      return result;
    } catch (error) {
      this.pendingRequests.delete(key);
      throw error;
    }
  }

  /**
   * Generate a cache key from method name and arguments
   */
  generateKey(methodName: string, args: any[]): string {
    const argsString = JSON.stringify(args, (key, value) => {
      // Handle special types
      if (value instanceof Date) return value.toISOString();
      if (typeof value === 'function') return 'function';
      if (value === undefined) return 'undefined';
      return value;
    });
    return `${methodName}:${argsString}`;
  }

  /**
   * Clean up stale pending requests
   */
  private cleanupStaleRequests(): void {
    const now = Date.now();
    for (const [key, request] of this.pendingRequests.entries()) {
      if (now - request.timestamp > this.config.ttl * 2) {
        this.pendingRequests.delete(key);
      }
    }
  }

  /**
   * Clear all pending requests
   */
  clear(): void {
    this.pendingRequests.clear();
  }

  /**
   * Get current pending request count
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }
}

// Singleton instance for global deduplication
export const globalDeduplicator = new RequestDeduplicator({
  enabled: true,
  ttl: 5000,
});
